import { type AgentSubscriber, HttpAgent, type RunAgentParameters } from '@ag-ui/client';
import { type Message } from '@ag-ui/core';

import { EventSubscriptionManager } from './EventSubscriptionManager';
import { isHttpError } from './errors';
import { sanitizeMediaMap } from './sanitizeMediaMap';
import { type AgentState, type ThreadData } from './store/slices/createChatSlice';
import { useRunErrorStore } from './store/useRunErrorStore';
import { useToolCallStatusStore } from './store/useToolCallStatusStore';
import { ALL_TOOLS, validateToolCallResult } from './tools';
import { type GetAuthToken, type ProgrammaticMessageOptions, type SendMessageParams } from './types/subscriptions';

export type AgentManagerConfig = {
  agentUrl: string;
  getAuthToken: GetAuthToken;
  getThreadData: (threadId: string) => ThreadData | null;
  onMessagesChanged: (threadId: string, messages: Message[]) => void;
  onStateChanged: (threadId: string, state: AgentState) => void;
  resourceId: string;
  workspaceId: string;
};

export type RunningStateListener = () => void;

export class AgentBusyError extends Error {
  constructor() {
    super('The agent is already working on your previous request.');
    this.name = 'AgentBusyError';
  }
}

const HTTP_UNAUTHORIZED = 401;
const HTTP_FORBIDDEN = 403;
const DEFAULT_EMPTY_STATE: AgentState = null;
const DEFAULT_EMPTY_MESSAGES: Message[] = [];

export class AgentManager {
  private agents = new Map<string, HttpAgent>();
  private subscriptions = new Map<string, { unsubscribe: () => void }>();
  private programmaticOptions = new Map<string, ProgrammaticMessageOptions>();
  private runningThreads = new Set<string>();
  private runningStateListeners = new Set<RunningStateListener>();
  private pendingBusyErrors = new Set<string>();
  private lastKnownMessages = new Map<string, Message[]>();
  private preRunMessages = new Map<string, Message[]>();
  private config: AgentManagerConfig;

  public readonly eventSubscriptions = new EventSubscriptionManager();

  constructor(config: AgentManagerConfig) {
    this.config = config;
  }

  /** Update the resource (canvas) ID without recreating the manager. Used during draft→real canvas commit. */
  setResourceId(resourceId: string): void {
    this.config = { ...this.config, resourceId };
  }

  subscribeToRunningState(listener: RunningStateListener): () => void {
    this.runningStateListeners.add(listener);
    return () => {
      this.runningStateListeners.delete(listener);
    };
  }

  private notifyRunningStateChange(): void {
    // Snapshot to protect against modifications during iteration
    const listeners = Array.from(this.runningStateListeners);
    for (const listener of listeners) {
      listener();
    }
  }

  getAgent(threadId: string): HttpAgent {
    const existing = this.agents.get(threadId);

    if (existing) {
      return existing;
    }

    const threadData = this.config.getThreadData(threadId);

    const agent = new HttpAgent({
      headers: {
        'x-amzn-bedrock-agentcore-runtime-session-id': threadId,
      },
      initialMessages: threadData?.messages ?? DEFAULT_EMPTY_MESSAGES,
      initialState: threadData?.state ?? DEFAULT_EMPTY_STATE,
      threadId,
      url: this.config.agentUrl,
    });

    this.agents.set(threadId, agent);
    this.lastKnownMessages.set(
      threadId,
      (threadData?.messages ?? DEFAULT_EMPTY_MESSAGES).filter((m) => m.role !== 'system'),
    );
    this.subscribeToAgent(threadId, agent);

    return agent;
  }

  removeAgent(threadId: string): void {
    const subscription = this.subscriptions.get(threadId);

    if (subscription) {
      subscription.unsubscribe();
      this.subscriptions.delete(threadId);
    }

    this.agents.delete(threadId);
    this.runningThreads.delete(threadId);
    this.programmaticOptions.delete(threadId);
    this.lastKnownMessages.delete(threadId);
    this.preRunMessages.delete(threadId);
    this.pendingBusyErrors.delete(threadId);
  }

  isRunning(threadId: string): boolean {
    return this.runningThreads.has(threadId);
  }

  async sendMessage({ threadId, messages, context, programmaticOptions }: SendMessageParams): Promise<void> {
    if (this.runningThreads.has(threadId)) {
      throw new AgentBusyError();
    }

    const agent = this.getAgent(threadId);

    useRunErrorStore.getState().clearRunError(threadId);

    try {
      this.runningThreads.add(threadId);
      if (programmaticOptions) {
        this.programmaticOptions.set(threadId, programmaticOptions);
      }
      this.notifyRunningStateChange();

      // Snapshot messages before adding new ones so we can revert if the agent is busy
      this.preRunMessages.set(threadId, this.lastKnownMessages.get(threadId) ?? []);

      for (const message of messages) {
        agent.addMessage(message);
      }

      agent.setState({
        ...agent.state,
        canvasId: this.config.resourceId,
        workspaceId: this.config.workspaceId,
      });

      const params: RunAgentParameters = {
        context,
        tools: ALL_TOOLS,
      };

      const token = await this.config.getAuthToken();
      agent.headers.Authorization = `Bearer ${token}`;

      try {
        await agent.runAgent(params);
      } catch (error) {
        if (isHttpError(error) && (error.status === HTTP_UNAUTHORIZED || error.status === HTTP_FORBIDDEN)) {
          // Token may have expired between fetch and request
          const freshToken = await this.config.getAuthToken({ forceRefresh: true });
          agent.headers.Authorization = `Bearer ${freshToken}`;

          await agent.runAgent(params);
        } else {
          throw error;
        }
      }

      if (this.pendingBusyErrors.has(threadId)) {
        this.pendingBusyErrors.delete(threadId);
        // Revert chat history to pre-run state (remove the failed user message)
        const snapshot = this.preRunMessages.get(threadId) ?? [];
        this.config.onMessagesChanged(threadId, snapshot);
        this.preRunMessages.delete(threadId);
        // Evict the stale agent so the next attempt creates a fresh one
        this.removeAgent(threadId);
        throw new AgentBusyError();
      }
    } finally {
      this.runningThreads.delete(threadId);
      this.programmaticOptions.delete(threadId);
      this.notifyRunningStateChange();
    }
  }

  dispose(): void {
    for (const threadId of this.agents.keys()) {
      this.removeAgent(threadId);
    }
    this.eventSubscriptions.dispose();
  }

  private handleRunError(threadId: string, error: { message: string; code?: string }): void {
    useRunErrorStore.getState().setRunError(threadId, error);
    useToolCallStatusStore.getState().failToolCalls(threadId);
  }

  private handleRunFailure(threadId: string, error: Error): void {
    this.handleRunError(threadId, {
      code: 'UNKNOWN',
      message: error.message,
    });
  }

  private subscribeToAgent(threadId: string, agent: HttpAgent): void {
    const subscriber: AgentSubscriber = {
      onMessagesChanged: (params) => {
        const visibleMessages = params.messages.filter((msg) => msg.role !== 'system');
        this.lastKnownMessages.set(threadId, visibleMessages);
        this.config.onMessagesChanged(threadId, visibleMessages);
        this.eventSubscriptions.notifyMessagesChanged(threadId, visibleMessages);
      },
      onRunErrorEvent: (params) => {
        // The server sends "The agent is already processing an invocation" when a concurrent
        // request is detected. Matched by message content because the ag-ui RunErrorEvent
        // does not include a stable error code for this condition.
        if (params.event.message?.includes('already processing an invocation')) {
          this.pendingBusyErrors.add(threadId);
        } else {
          this.handleRunError(threadId, {
            code: params.event.code,
            message: params.event.message,
          });
        }
      },
      onRunFailed: (params) => {
        this.handleRunFailure(threadId, params.error);
      },
      onStateChanged: (params) => {
        const state = params.state?.mediaMap
          ? { ...params.state, mediaMap: sanitizeMediaMap(params.state.mediaMap) }
          : params.state;
        this.config.onStateChanged(threadId, state);
        this.eventSubscriptions.notifyStateChanged(threadId, state);
      },
      onToolCallResultEvent: (params) => {
        useToolCallStatusStore.getState().completeToolCall(threadId, params.event.toolCallId);

        const validationResult = validateToolCallResult(params.event.toolCallId, params.event.content, params.messages);

        if (validationResult.success) {
          this.eventSubscriptions.notifyToolCallResult(threadId, {
            event: params.event,
            programmaticOptions: this.programmaticOptions.get(threadId),
            ...validationResult.params,
          });
        }
      },
      onToolCallStartEvent: (params) => {
        useToolCallStatusStore.getState().startToolCall(threadId, params.event.toolCallId, params.event.toolCallName);
      },
    };

    const subscription = agent.subscribe(subscriber);
    this.subscriptions.set(threadId, subscription);
  }
}
